import { ChangeDetectionStrategy, Component, Input, output } from '@angular/core';

import type { Candidate } from '@recruitee/candidate-types';
import { TranslatePipe } from '@recruitee/i18n';
import type {
  Admin,
  HiddenResultPreview,
  InterviewResult,
  ResultGroup,
  ScorecardsResult,
} from '@recruitee/interview-results-types';
import type { Guest } from '@recruitee/notes-types';
import type { Placement } from '@recruitee/placements-types';
import type { Stage } from '@recruitee/stages-types';
import { BoxComponent, DividerComponent, HeadingComponent } from '@recruitee/user-interface';
import { groupByKey, uniqByKey } from '@recruitee/utils';

import { InterviewResultSummaryOverallComponent } from './interview-result-summary-overall/interview-result-summary-overall.component';
import { InterviewResultSummaryScorecardsComponent } from './interview-result-summary-scorecards/interview-result-summary-scorecards.component';

type InterviewRating = {
  count: number;
  rating: string;
  admins: Array<InterviewResult['admin']>;
  guests: Array<Guest | undefined>;
};

@Component({
  selector: 'rt-interview-result-summary-list',
  templateUrl: 'interview-result-summary-list.component.html',
  styleUrls: ['./interview-result-summary-list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BoxComponent,
    InterviewResultSummaryOverallComponent,
    HeadingComponent,
    DividerComponent,
    InterviewResultSummaryScorecardsComponent,
    TranslatePipe,
  ],
})
export class InterviewResultSummaryListComponent {
  @Input() public scorecardsResults: ScorecardsResult[];
  @Input() public lastUpdate: string | null;
  @Input() public candidate: Candidate;
  @Input() public results?: InterviewResult[];
  @Input() public hiddenResultsPreview?: HiddenResultPreview[];
  @Input() public offerId?: number;
  @Input() public currentUserAvailablePlacements: Placement[] = [];
  @Input() public hasAccessToAllJobOffers?: boolean;

  public summaryResultCounterClick = output();

  public groups: ResultGroup[] = [];

  private ratingsOrder: string[] = ['strong_no', 'no', 'neutral', 'yes', 'strong_yes'];

  public ngOnChanges(): void {
    const stages = this.getStagesWithOffer(this.results || [], this.hiddenResultsPreview || []);
    this.groups = this.getGroups(this.results || [], stages, this.hiddenResultsPreview || []);
  }

  private getStagesWithOffer(
    results: InterviewResult[],
    hiddenResultsPreview: HiddenResultPreview[],
  ): Stage[] {
    const stages = [...results, ...hiddenResultsPreview].map(r => r.stage!).filter(r => r);

    return uniqByKey(stages, 'id');
  }

  private getGroups(
    results: InterviewResult[],
    stages: Stage[],
    hiddenResultsPreview: HiddenResultPreview[],
  ): ResultGroup[] {
    const mergedResults = [...results, ...hiddenResultsPreview];

    return (
      Object.entries(this.groupBy(mergedResults, 'stageId'))
        // Object.entries returns objects' keys as strings, meanwhile mapStage expects a number
        .reverse()
        .map(entry => [Number(entry[0]), entry[1]])
        .map(this.mapStage.bind(this, stages))
    );
  }

  private getOfferName(results: Array<InterviewResult | HiddenResultPreview>): string | null {
    const offerName = results && results[0] && results[0].offer && results[0].offer.title;

    return offerName ? offerName : null;
  }

  private getOfferId(results: Array<InterviewResult | HiddenResultPreview>): number | null {
    return results?.[0]?.offerId ?? null;
  }

  private getStageName(stages: Stage[], stageId: number): string | null {
    const stage = stages.find(s => s && s.id === stageId);

    return stage && stage.name ? stage.name : null;
  }

  private getRatings(results: InterviewResult[]): InterviewRating[] {
    return Object.entries(groupByKey(results, 'rating'))
      .map(entry => this.mapResult(entry))
      .sort((a, b) => (this.compareRatings(a) > this.compareRatings(b) ? 1 : -1));
  }

  private mapStage(
    stages: Stage[],
    [rating, results]: [number, Array<InterviewResult | HiddenResultPreview>],
  ): {
    stage: string;
    offer: string;
    offerId: number;
    ratings: InterviewRating[];
    hiddenRatings: Omit<InterviewRating, 'rating'>;
  } {
    return {
      stage: this.getStageName(stages, rating)!,
      offer: this.getOfferName(results)!,
      offerId: this.getOfferId(results)!,
      ratings: this.getRatings(
        <InterviewResult[]>results.filter((result: any) => typeof result.rating !== 'undefined'),
      ),
      hiddenRatings: (<HiddenResultPreview[]>(
        results.filter((result: any) => typeof result.rating === 'undefined')
      )).map(result => ({
        count: result.count,
        admins: result.admins,
        guests: result.guests || [],
      }))[0],
    };
  }

  private mapResult([rating, result]: [string, InterviewResult[]]): InterviewRating {
    return {
      count: result.length,
      rating: <string>this.convertType(rating)!,
      admins: uniqByKey(
        result.map(r => r.admin || <Admin>{ id: null }),
        'id',
      ).filter(value => (value?.id ?? -1) > 0),
      guests: uniqByKey(
        result.map(r => r.guest || <Guest>{ id: -1 }),
        'id',
      ).filter(value => value.id > 0),
    };
  }

  private compareRatings = (rating: InterviewRating) => this.ratingsOrder.indexOf(rating.rating);

  private groupBy(
    results: Array<InterviewResult | HiddenResultPreview>,
    key: keyof InterviewResult | keyof HiddenResultPreview,
  ): { [key: string]: InterviewResult[] } {
    return (<any[]>results).reduce<Record<string, InterviewResult[]>>((agg, result) => {
      agg[result[key]] = [...(agg[result[key]] || []), result];
      return agg;
    }, {});
  }

  private convertType(value: string): string | undefined | null | boolean | number {
    const values = { undefined: undefined, null: null, true: true, false: false };
    const isNumber = !isNaN(+value);
    return (
      (isNumber && +value) ||
      (!(value in values) && value) ||
      values[<'undefined' | 'null' | 'true' | 'false'>value]
    );
  }
}
